import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';

export interface BookingFilters {
  propertyIds?: string[];
  originId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  guestSearch?: string;
  status?: string;
  organizationIds?: string[];
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface BookingResult {
  id: string;
  booking_id: string;
  property_id: string;
  property_short_name: string;
  guest_id: string | null;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  origin_id: string;
  origin_name: string | null;
  check_in: string;
  check_out: string;
  nights: number;
  total_guests: number;
  children: number | null;
  total_payment: number;
  cleaning_fee: number | null;
  base_payment: number | null;
  pets_fee: number | null;
  early_checkin_fee: number | null;
  late_checkout_fee: number | null;
  other_fees: number | null;
  estimated_payout: number | null;
  stay_completed: boolean | null;
  paid: boolean | null;
  status: 'Reservada' | 'Cancelada';
  note: string | null;
  created_at: string;
  updated_at: string;
  total_count: number;
}

export const useBookingsFiltered = () => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Filter states
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedOrigin, setSelectedOrigin] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [guestSearch, setGuestSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Debounce guest search to avoid excessive backend calls
  const debouncedGuestSearch = useDebounce(guestSearch, 300);

  // Construct backend filters
  const backendFilters: BookingFilters = useMemo(() => ({
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    originId: selectedOrigin !== 'all' ? selectedOrigin : undefined,
    dateFrom,
    dateTo,
    guestSearch: debouncedGuestSearch || undefined,
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
    page: currentPage,
    pageSize,
    orderBy: 'check_in',
    orderDirection: 'desc',
  }), [
    selectedProperties,
    selectedOrigin,
    dateFrom,
    dateTo,
    debouncedGuestSearch,
    selectedStatus,
    selectedOrganizations,
    currentPage,
    pageSize,
  ]);

  // Fetch bookings using RPC
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['bookings-filtered', backendFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_bookings', {
        p_property_ids: backendFilters.propertyIds || null,
        p_origin_id: backendFilters.originId || null,
        p_date_from: backendFilters.dateFrom?.toISOString().split('T')[0] || null,
        p_date_to: backendFilters.dateTo?.toISOString().split('T')[0] || null,
        p_guest_search: backendFilters.guestSearch || null,
        p_status: backendFilters.status || null,
        p_organization_ids: backendFilters.organizationIds || null,
        p_page: backendFilters.page || 1,
        p_page_size: backendFilters.pageSize || 100,
        p_order_by: backendFilters.orderBy || 'check_in',
        p_order_direction: backendFilters.orderDirection || 'desc',
      });

      if (error) throw error;
      return data as BookingResult[];
    },
  });

  const bookings = data || [];
  const totalCount = bookings.length > 0 ? bookings[0].total_count : 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters = Boolean(
    guestSearch ||
    selectedProperties.length > 0 ||
    selectedOrganizations.length > 0 ||
    selectedOrigin !== 'all' ||
    selectedStatus !== 'all' ||
    dateFrom ||
    dateTo
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  const resetFilters = () => {
    setGuestSearch('');
    setSelectedProperties([]);
    setSelectedOrganizations([]);
    setSelectedOrigin('all');
    setSelectedStatus('all');
    setDateFrom(undefined);
    setDateTo(undefined);
    setCurrentPage(1);
  };

  // Filter state change handlers that reset page
  const handleGuestSearchChange = (value: string) => {
    setGuestSearch(value);
    setCurrentPage(1);
  };

  const handlePropertiesChange = (value: string[]) => {
    setSelectedProperties(value);
    setCurrentPage(1);
  };

  const handleOrganizationsChange = (value: string[]) => {
    setSelectedOrganizations(value);
    setCurrentPage(1);
  };

  const handleOriginChange = (value: string) => {
    setSelectedOrigin(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    setCurrentPage(1);
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    setCurrentPage(1);
  };

  return {
    // Data
    bookings,
    totalCount,
    totalPages,
    isLoading,
    error,
    refetch,

    // Pagination
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // Filters
    guestSearch,
    selectedProperties,
    selectedOrganizations,
    selectedOrigin,
    selectedStatus,
    dateFrom,
    dateTo,
    hasActiveFilters,

    // Filter handlers
    handleGuestSearchChange,
    handlePropertiesChange,
    handleOrganizationsChange,
    handleOriginChange,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    resetFilters,
  };
};
